import { combineEpics } from 'redux-observable';
import { from, of, pipe } from 'rxjs';
import { filter, map, mapTo, mergeMap, switchMap, tap, throttleTime } from 'rxjs/operators';
import { User } from 'logic/api-models/api-models';
import { authenticationSlice } from '../authentication.slice';
import { validateRegisterPhoneForm } from 'components/authentication/forms/register-phone/register-phone.logic';
import { API_MULTIPLY_CALLS_TIMEOUT_MS } from 'logic/store/const';
import { getAuthenticatedUser } from '../authentication.selectors';
import {
  mapPhoneToCountryCodeAndNumber,
  parsePhoneNumberForPayload,
} from 'logic/phone/phone.mapper';
import { modalSlice } from 'logic/store/modal/modal.slice';
import { AuthenticationStepKey } from '../authentication.model';
import { shouldUpdateUserPhoneNumber } from '../authentication.logic';
import {
  validateVerifyPhoneForm,
  VERIFY_PHONE_FORM_NAME,
} from 'components/authentication/forms/verify-phone/verify-phone.logic';
import { translate, translationKeys } from 'logic/translations/translations.service';
import { change, initialize } from 'redux-form';
import { stepFlowSlice } from 'logic/store/step-flow/step-flow.slice';
import {
  UpdatePhoneNumberStep,
  UPDATE_PHONE_NUMBER_STEP_FLOW_NAME,
} from 'src/pages-all/user/user-profile/components/phone-number-section/phone-number-section.logic';
import { DEFAULT_PHONE_COUNTRY_CODE } from 'components/country-code/country-code.const';
import {
  UpdatePhoneNumberField,
  UPDATE_PHONE_NUMBER_FORM_NAME,
  validateUpdatedPhoneNumberForm,
} from 'src/pages-all/user/user-profile/components/phone-number-section/update-phone-number.logic';
import {
  VerifyPhoneNumberField,
  VerifyPhoneNumberFormValues,
  VERIFY_PHONE_NUMBER_FORM_NAME,
} from 'src/pages-all/user/user-profile/components/phone-number-section/verify-phone-number.logic';
import { badRequestErrorDefaultHandler } from 'logic/error/bad-request.error';
import { RootEpic } from 'app/app.epics.type';
import { Dispatch } from 'redux';
import { AuthenticationApi } from '../authentication.api';
import { logSlice } from 'logic/store/log/log.slice';
import { REGISTER_PHONE_FORM_NAME } from 'components/authentication/forms/register-phone/register-phone.const';

const sendPhoneVerificationCode$: RootEpic = (
  action$,
  state$,
  { dispatch, managed, badRequestFormErrorHandler, ofValidReduxForm, authenticationApi }
) =>
  action$.pipe(
    filter(authenticationSlice.actions.sendPhoneVerificationCode.match),
    ofValidReduxForm(REGISTER_PHONE_FORM_NAME, validateRegisterPhoneForm),
    throttleTime(API_MULTIPLY_CALLS_TIMEOUT_MS),
    managed(
      authenticationSlice.actions.sendPhoneVerificationCode,
      pipe(
        switchMap((action) => {
          const user = getAuthenticatedUser(state$.value);
          const newPhoneNumber = parsePhoneNumberForPayload(
            action.payload.formValues?.phoneCountryCode,
            action.payload.formValues?.phoneNumber
          );
          return updateUserPhoneOrResendVerificationSms(
            user,
            newPhoneNumber,
            dispatch,
            authenticationApi
          );
        })
      ),
      [badRequestFormErrorHandler(REGISTER_PHONE_FORM_NAME)]
    ),
    mapTo(
      modalSlice.actions.openAuthenticationModal({
        stepKey: AuthenticationStepKey.VerifyPhoneCode,
      })
    )
  );

const updateUserPhoneOrResendVerificationSms = (
  user: User | undefined,
  phoneNumber: string,
  dispatch: Dispatch,
  authenticationApi: AuthenticationApi
) =>
  shouldUpdateUserPhoneNumber(user?.phoneNumber, phoneNumber)
    ? from(authenticationApi.updateUser(user?._id || '', { phoneNumber })).pipe(
        switchMap(() => from(authenticationApi.fetchCurrentUser())),
        tap((response) =>
          dispatch(
            authenticationSlice.actions.setUser({
              user: response.data.data,
            })
          )
        )
      )
    : from(authenticationApi.resendVerificationSms({ phoneNumber }));

const verifyPhone$: RootEpic = (
  action$,
  state$,
  { managed, badRequestFormErrorHandler, ofValidReduxForm, authenticationApi }
) =>
  action$.pipe(
    filter(authenticationSlice.actions.verifyPhone.match),
    ofValidReduxForm(VERIFY_PHONE_FORM_NAME, validateVerifyPhoneForm),
    throttleTime(API_MULTIPLY_CALLS_TIMEOUT_MS),
    managed(
      authenticationSlice.actions.verifyPhone,
      pipe(
        switchMap((action) =>
          from(
            authenticationApi.activatePhoneNumber({
              phoneNumber: getAuthenticatedUser(state$.value)?.phoneNumber || '',
              verificationCode: action.payload.formValues?.verificationCode || '',
            })
          )
        ),
        switchMap(() => from(authenticationApi.fetchCurrentUser()))
      ),
      [badRequestFormErrorHandler(VERIFY_PHONE_FORM_NAME)]
    ),
    map((response) => response.data.data),
    mergeMap((user) =>
      from([
        authenticationSlice.actions.setUser({ user }),
        logSlice.actions.logSuccess({
          message: translate(translationKeys.confirmation.yourPhoneNumberVerifiedSuccessfully),
        }),
        shouldSelectUserType(user)
          ? modalSlice.actions.openAuthenticationModal({
              stepKey: AuthenticationStepKey.SelectUserType,
            })
          : modalSlice.actions.closeAuthenticationModal(),
      ])
    )
  );

const shouldSelectUserType = (user: User | undefined) => !user?.accountType;

const updatePhoneInitialize$: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(authenticationSlice.actions.updatePhoneInitialize.match),
    mergeMap(() => {
      const user = getAuthenticatedUser(state$.value);
      const phone = mapPhoneToCountryCodeAndNumber(user?.phoneNumber);
      return of(
        stepFlowSlice.actions.setFlowStep({
          stepFlowName: UPDATE_PHONE_NUMBER_STEP_FLOW_NAME,
          flowStepKey: UpdatePhoneNumberStep.EnterPhone,
        }),
        initialize(UPDATE_PHONE_NUMBER_FORM_NAME, {
          [UpdatePhoneNumberField.phoneCountryCode]: phone?.code || DEFAULT_PHONE_COUNTRY_CODE,
          [UpdatePhoneNumberField.phoneNumber]: phone?.number,
        })
      );
    })
  );

const updatePhoneFinalize$: RootEpic = (action$) =>
  action$.pipe(
    filter(authenticationSlice.actions.updatePhoneFinalize.match),
    map(() =>
      stepFlowSlice.actions.resetFlowStep({
        stepFlowName: UPDATE_PHONE_NUMBER_STEP_FLOW_NAME,
      })
    )
  );

const updatePhone$: RootEpic = (
  action$,
  state$,
  { dispatch, managed, badRequestFormErrorHandler, ofValidReduxForm, authenticationApi }
) =>
  action$.pipe(
    filter(authenticationSlice.actions.updatePhone.match),
    ofValidReduxForm(UPDATE_PHONE_NUMBER_FORM_NAME, validateUpdatedPhoneNumberForm),
    throttleTime(API_MULTIPLY_CALLS_TIMEOUT_MS),
    managed(
      authenticationSlice.actions.updatePhone,
      pipe(
        switchMap((action) => {
          const user = getAuthenticatedUser(state$.value);
          const phoneNumber = parsePhoneNumberForPayload(
            action.payload.formValues?.phoneCountryCode,
            action.payload.formValues?.phoneNumber
          );
          return updateUserPhoneOrResendVerificationSms(
            user,
            phoneNumber,
            dispatch,
            authenticationApi
          );
        }),
        map(() =>
          stepFlowSlice.actions.setFlowStep({
            stepFlowName: UPDATE_PHONE_NUMBER_STEP_FLOW_NAME,
            flowStepKey: UpdatePhoneNumberStep.VerifyCode,
          })
        )
      ),
      [badRequestFormErrorHandler(UPDATE_PHONE_NUMBER_FORM_NAME)]
    )
  );

const verifyUpdatedPhone$: RootEpic = (
  action$,
  state$,
  {
    dispatch,
    managed,
    badRequestFormErrorHandler,
    ofValidReduxForm,
    authenticationApi,
    mapFormValues,
  }
) =>
  action$.pipe(
    filter(authenticationSlice.actions.verifyUpdatedPhone.match),
    ofValidReduxForm(VERIFY_PHONE_NUMBER_FORM_NAME, validateVerifyPhoneForm),
    throttleTime(API_MULTIPLY_CALLS_TIMEOUT_MS),
    managed(
      authenticationSlice.actions.verifyUpdatedPhone,
      pipe(
        mapFormValues<VerifyPhoneNumberFormValues>(VERIFY_PHONE_NUMBER_FORM_NAME),
        switchMap((formValues) => {
          return from(
            authenticationApi.activatePhoneNumber({
              phoneNumber: getAuthenticatedUser(state$.value)?.phoneNumber || '',
              verificationCode: formValues?.verificationCode || '',
            })
          );
        }),
        switchMap(() => from(authenticationApi.fetchCurrentUser()))
      ),
      [
        resetCodeOnAnyError(dispatch),
        badRequestFormErrorHandler(VERIFY_PHONE_NUMBER_FORM_NAME),
        badRequestErrorDefaultHandler,
      ]
    ),
    mergeMap((response) =>
      of(
        authenticationSlice.actions.setUser({ user: response.data.data }),
        stepFlowSlice.actions.resetFlowStep({
          stepFlowName: UPDATE_PHONE_NUMBER_STEP_FLOW_NAME,
        }),
        logSlice.actions.logSuccess({
          message: translate(translationKeys.confirmation.yourPhoneNumberVerifiedSuccessfully),
        })
      )
    )
  );

const resetCodeOnAnyError = (dispatch: Dispatch) => () => {
  dispatch(
    change(VERIFY_PHONE_NUMBER_FORM_NAME, VerifyPhoneNumberField.verificationCode, undefined)
  );
  return false;
};

export const registerPhoneEpics$ = combineEpics(
  sendPhoneVerificationCode$,
  verifyPhone$,
  updatePhoneInitialize$,
  updatePhoneFinalize$,
  updatePhone$,
  verifyUpdatedPhone$
);
